import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { ButtonModel } from '@notino/react-styleguide';

import { appendQueryParam } from '@utils/url';

import { REVIEW_LOGIN_QUERY_PARAM } from '../../../../utils/shouldOpenReviewModal';
import { messages } from '../messages';
import { ReviewableProductWithUrl } from '../useReviewableProducts';

import {
  Footer,
  Lead,
  MyReviewsLink,
  ProductDescription,
  ProductImage,
  ProductInfo,
  ProductLink,
  ProductList,
  ProductName,
  ProductRow,
  SectionSubtitle,
  SectionTitle,
  SectionWrapper,
  WriteReviewButton,
} from './styled';

type ProductClickHandler = (
  event: React.SyntheticEvent,
  payload: {
    product: ReviewableProductWithUrl['product'];
    position: number;
  }
) => void;

type Props = {
  products: ReviewableProductWithUrl[];
  onMyReviewsClick: (event: React.SyntheticEvent) => void;
  onWriteReviewClick: ProductClickHandler;
  onProductClick: ProductClickHandler;
};

export const ReviewableProductsSection = ({
  products,
  onMyReviewsClick,
  onWriteReviewClick,
  onProductClick,
}: Props) => {
  if (products.length === 0) {
    return null;
  }

  return (
    <SectionWrapper data-cypress="reviewsModal-reviewableProducts">
      <SectionTitle>
        <FormattedMessage {...messages.continueReviewingTitle} />
      </SectionTitle>
      <SectionSubtitle>
        <FormattedMessage {...messages.continueReviewingSubtitle} />
      </SectionSubtitle>

      <ProductList>
        {products.map(({ catalogId, product }, index) => {
          const { url } = product;
          const writeReviewUrl = appendQueryParam(
            url,
            REVIEW_LOGIN_QUERY_PARAM,
            'true'
          );
          const altText = `${product.brand?.name ?? ''} ${
            product.name ?? ''
          }`.trim();
          const selection = { product, position: index + 1 };
          const handleProductClick = (event: React.SyntheticEvent) =>
            onProductClick(event, selection);

          return (
            <ProductRow key={catalogId}>
              <Lead>
                <ProductLink
                  href={url}
                  aria-label={altText}
                  onClick={handleProductClick}
                >
                  <ProductImage
                    src={product.imageUrl ?? ''}
                    sourceKey="list"
                    alt={altText}
                  />
                </ProductLink>

                <ProductInfo href={url} onClick={handleProductClick}>
                  <ProductName>
                    {product.brand?.name ? `${product.brand.name} ` : ''}
                    <strong>{product.name}</strong>
                  </ProductName>
                  {product.description && (
                    <ProductDescription>
                      {product.description}
                    </ProductDescription>
                  )}
                </ProductInfo>
              </Lead>

              <WriteReviewButton
                href={writeReviewUrl}
                onClick={(event: React.SyntheticEvent) =>
                  onWriteReviewClick(event, selection)
                }
                buttonStyle={ButtonModel.Styles.secondary}
                buttonSize={ButtonModel.Sizes.small}
              >
                <FormattedMessage {...messages.writeReview} />
              </WriteReviewButton>
            </ProductRow>
          );
        })}
      </ProductList>

      <Footer>
        <FormattedMessage
          {...messages.myReviewsFooterText}
          values={{
            link: (chunks: React.ReactNode) => (
              <MyReviewsLink
                href="/mynotino/my-reviews"
                onClick={onMyReviewsClick}
              >
                {chunks}
              </MyReviewsLink>
            ),
          }}
        />
      </Footer>
    </SectionWrapper>
  );
};
